import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { AlertTriangle, Activity, ArrowUpRight, Loader2 } from "lucide-react";

const SEV_COLOR = {
  high: "#EF4444",
  medium: "#F59E0B",
  low: "#94A3B8",
};

export default function StrategicAlerts() {
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/intel/alerts?limit=8"), api.get("/intel/health")])
      .then(([a, h]) => {
        setData(a.data);
        setHealth(h.data);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="nexus-card p-5 flex items-center gap-2 text-slate-400 font-mono text-xs tracking-widest">
        <Loader2 className="w-3 h-3 animate-spin" /> SCANNING…
      </div>
    );
  }
  if (!data) return null;

  const score = health?.score ?? 0;
  const grade = health?.grade ?? "—";

  return (
    <div className="nexus-card p-5" data-testid="strategic-alerts">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Activity className="w-3 h-3" style={{ color: "var(--gold-400)" }} strokeWidth={1.6} />
            <span className="label-eyebrow">// Strategic Alerts</span>
          </div>
          <h3 className="font-display text-lg text-white">Workflow Intelligence</h3>
        </div>
        <div className="text-right">
          <div className="font-mono text-[10px] text-slate-500 tracking-widest">HEALTH</div>
          <div
            className="font-display text-2xl"
            style={{ color: score >= 70 ? "var(--gold-400)" : score >= 50 ? "#F59E0B" : "#EF4444" }}
            data-testid="org-health-score"
          >
            {score}
            <span className="text-xs ml-1 text-slate-500">/100 · {grade}</span>
          </div>
        </div>
      </div>

      {/* Severity row */}
      <div className="flex gap-2 mb-4">
        {["high", "medium", "low"].map((s) => (
          <div key={s} className="flex-1 p-2.5 border border-white/5 bg-white/[0.02]">
            <div className="font-mono text-[10px] tracking-widest" style={{ color: SEV_COLOR[s] }}>
              {s.toUpperCase()}
            </div>
            <div className="font-display text-xl text-white mt-0.5" data-testid={`alert-count-${s}`}>
              {data.counts[s] ?? 0}
            </div>
          </div>
        ))}
      </div>

      {/* Alert list */}
      {data.alerts.length === 0 ? (
        <div className="py-8 text-center">
          <div className="w-10 h-10 mx-auto rounded-full flex items-center justify-center mb-2" style={{ background: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.3)" }}>
            <Activity className="w-4 h-4 text-emerald-400" strokeWidth={1.5} />
          </div>
          <div className="text-sm text-white">All clear</div>
          <div className="text-[11px] text-slate-500 mt-1">No active workflow risks detected.</div>
        </div>
      ) : (
        <div className="space-y-2">
          {data.alerts.slice(0, 5).map((a) => (
            <Link
              key={a.id}
              to={a.link || "/"}
              data-testid={`alert-${a.id}`}
              className="flex items-start gap-3 p-3 border border-white/5 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/15 transition-all group"
            >
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: SEV_COLOR[a.severity] }} strokeWidth={1.6} />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{a.title}</div>
                <div className="text-[11px] text-slate-400 mt-0.5 leading-snug line-clamp-2">{a.body}</div>
              </div>
              <ArrowUpRight className="w-3 h-3 text-slate-600 group-hover:text-[#D4AF37] transition-colors" />
            </Link>
          ))}
        </div>
      )}

      {data.alerts.length > 5 && (
        <div className="mt-3 text-center">
          <span className="font-mono text-[10px] text-slate-500 tracking-widest">
            +{data.alerts.length - 5} MORE — REVIEW IN AFFECTED MODULES
          </span>
        </div>
      )}
    </div>
  );
}
